/**
 * The account card and the Family / Team / League panel, at the top of Settings.
 *
 * Three states, drawn into #accountRoot:
 *   1. signed out  ask for a one-time sign-in link, then wait on the inbox
 *   2. signed in   email, display name, plan, member since, email recap, sign out
 *   3. groups      the family, team or league they belong to, or the three plans
 *
 * Sharing lives here, on the player's own account, because the player is the one
 * who decides what a group can see and for how long. Nothing is shared by default
 * and every share expires on its own.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* The five things a group can ask to see. worker/orgs.js accepts exactly these. */
const SCOPES = [
  { id: 'sessions', label: 'Training sessions', note: 'Shots, makes and form scores from your sessions.' },
  { id: 'journal', label: 'Journal', note: 'Your written entries. Talks with your companion never go out.' },
  { id: 'nutrition', label: 'Nutrition', note: 'A seven day summary of meals, calories, protein and water. Not your photos.' },
  { id: 'body', label: 'Body log', note: 'Your last seven logs: weight, height, sleep and resting heart rate.' },
  { id: 'vault', label: 'Vault clips', note: 'The recordings you saved to your Cloud Vault.' },
];
const DAYS = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, 'One year']];
const FREQ = [['off', 'Off'], ['daily', 'Every day'], ['weekly', 'Every week'], ['monthly', 'Every month']];
const GROUP_PLANS = ['family', 'team', 'league'];
const ROLE_WORD = { owner: 'Owner', coach: 'Coach', parent: 'Parent', player: 'Player' };
const VIEWER_ROLES = ['owner', 'coach', 'parent'];

let ctx = null;
let root = null;
let me = null;          // GET /api/auth/me
let orgs = [];          // GET /api/orgs -> orgs
let plans = {};         // GET /api/orgs -> plans
let shares = [];        // GET /api/orgs -> shares (this player's own)
let whoami = {};        // GET /api/orgs -> me: this account's own age, consent and coach verification
let wards = [];         // GET /api/orgs/wards -> the children this account is the confirmed parent of
let meError = '';
let orgError = '';
let sentTo = '';        // where the last sign-in link went
let typedEmail = '';    // kept across repaints so a failed send does not wipe it
const drafts = new Map();   // orgId -> { scopes:Set, days:Number }
const brains = new Map();   // orgId -> { players } | { error } , loaded on demand
const audits = new Map();   // orgId -> array | { error } , loaded on demand

/* ------------------------------------------------------------------ helpers */
const fmtDay = (iso) => {
  const d = new Date(iso || '');
  return Number.isNaN(d.getTime()) ? 'an unknown date' : d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};
const fmtShort = (iso) => {
  const d = new Date(iso || '');
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const cut = (s, n) => { const t = String(s ?? '').trim(); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };
// Dialog titles and toasts are set with textContent by ui.js and by the shell's
// toast, so they need no escaping. They do need trimming: a name is often typed
// with a trailing space and it shows up in the middle of a sentence.
const plain = (s) => String(s ?? '').trim();
const planName = (id) => plans[id]?.name || (id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Basic');
const myShare = (orgId) => shares.find((s) => s.orgId === orgId) || null;
const live = (share) => Boolean(share && share.scopes?.length && (share.until == null || new Date(share.until) > new Date()));
// A minor's sharing is the parent's decision: worker/orgs.js wants the parent PIN
// with every change (the same rule as scout grants, worker/visibility.js). The
// age that counts is one somebody chose: an unchosen band is not an adult.
const isMinor = () => { const s = ctx?.S?.settings; return !(s?.ageBandChosen && s.ageBand === 'adult'); };
// The server counts seats from the whole roster, including people this viewer may not see.
const seatsUsed = (o) => (Number.isFinite(o.seatsUsed) ? o.seatsUsed : o.members.filter((m) => m.role === 'player').length
  + (o.invites || []).filter((i) => i.role === 'player').length);
const REPORT_REASONS = [
  ['safety', 'Something they did made me feel unsafe'],
  ['private', 'They asked to talk in private or off Hooper Buddy'],
  ['gifts', 'They offered gifts, money or rides'],
  ['harassment', 'They are bothering me'],
  ['other', 'Something else'],
];

function draftFor(o) {
  let d = drafts.get(o.id);
  if (!d) {
    const s = myShare(o.id);
    d = { scopes: new Set(live(s) ? s.scopes : []), days: 30 };
    drafts.set(o.id, d);
  }
  return d;
}

/* -------------------------------------------------------------------- mount */
export function mount(c) {
  ctx = c;
  root = document.getElementById('accountRoot');
  // The shell points the rail's sign out button at the sales site. With accounts
  // in play it has to end the session first.
  const out = document.getElementById('btnOut');
  if (out) out.onclick = railSignOut;
  // worker/orgs.js sends an accepted invitee to /app#settings. Open it for them,
  // after every module has mounted.
  if (location.hash === '#settings') {
    setTimeout(() => { try { ctx.go('settings'); } catch (e) { console.error('open settings', e); } }, 0);
  }
}

export async function render() {
  if (!root) return;
  meError = ''; orgError = '';
  brains.clear(); audits.clear(); drafts.clear();
  try { me = await ctx.api('/api/auth/me'); }
  catch (e) { me = null; meError = e.message; }
  if (me && me.signedIn) await loadOrgs();
  else { orgs = []; shares = []; }
  paint();
}

async function loadOrgs() {
  try {
    const r = await ctx.api('/api/orgs');
    orgs = Array.isArray(r.orgs) ? r.orgs : [];
    plans = r.plans || {};
    shares = Array.isArray(r.shares) ? r.shares : [];
    whoami = r.me || {};
    orgError = '';
  } catch (e) { orgs = []; orgError = e.message; }
  try { const w = await ctx.api('/api/orgs/wards'); wards = Array.isArray(w.wards) ? w.wards : []; } catch (e) { wards = []; }
}

/* -------------------------------------------------------------------- paint */
function paint() {
  if (!root) return;
  // Repainting replaces the whole panel, which would throw the page back to the
  // top every time a panel opens. Put the player back where they were.
  const y = window.scrollY;
  let html = '';
  if (meError) html += card('Your account', `<p class="acct-err">${esc(meError)}</p>`);
  else if (!me || !me.signedIn) html += signedOutCard();
  else html += accountCard() + groupsHtml();
  root.innerHTML = html;
  bind();
  if (y) window.scrollTo(0, y);
}

const card = (title, inner, cls = '') =>
  `<div class="card acct ${cls}" style="margin-bottom:14px"><h3>${esc(title)}</h3>${inner}</div>`;

function signedOutCard() {
  if (sentTo) {
    return card('Check your inbox', `
      <p class="acct-lead">A sign-in link is on its way to <b>${esc(sentTo)}</b>.</p>
      <p class="acct-note">Open it on this device and you are in. The link works once and expires in fifteen minutes. If it is not there in a minute, look in the spam folder.</p>
      <div class="acct-row">
        <button class="btn" id="acctAgain">Send another link</button>
        <button class="btn ghost" id="acctOther">Use a different email</button>
      </div>
      <p class="acct-err" id="acctErr" hidden></p>`);
  }
  return card('Your account', `
    <p class="acct-lead">Sign in and your buddy follows you to every device.</p>
    <p class="acct-note">No password. We email you a link, it works once, and it expires in fifteen minutes.</p>
    <div class="acct-row">
      <input class="acct-input grow" id="acctEmail" type="email" autocomplete="email" placeholder="you@email.com" maxlength="200" value="${esc(typedEmail)}">
      <button class="btn primary" id="acctSend">Email me a link</button>
    </div>
    <p class="acct-note">The first time you sign in, everything you have already done on this device comes with you: your sessions, your journal, your vault and your companion. Nothing is lost.</p>
    <p class="acct-err" id="acctErr" hidden></p>`);
}

function accountCard() {
  const freq = me.digest?.frequency || 'weekly';
  return card('Your account', `
    <div class="acct-head">
      <div class="lab"><b>${esc(me.email)}</b><small>Member since ${esc(fmtDay(me.createdAt))}</small></div>
      <span class="pill hot">${esc(planName(me.plan))}</span>
    </div>
    <div class="srow">
      <div class="lab"><b>Display name</b><small>What your companion and your group call you.</small></div>
      <input class="acct-input" id="acctName" type="text" maxlength="60" value="${esc(me.name || '')}">
      <button class="btn" id="acctNameSave">Save</button>
    </div>
    <div class="srow">
      <div class="lab"><b>Email recap</b><small>A short write-up of how training is going, sent to you. Turn it off any time.</small></div>
      <select class="acct-input" id="acctDigest">
        ${FREQ.map(([v, l]) => `<option value="${v}"${v === freq ? ' selected' : ''}>${l}</option>`).join('')}
      </select>
    </div>
    <div class="acct-row"><button class="btn" id="acctOut">Sign out</button></div>`);
}

/* ------------------------------------------------------- family, team, league */
function groupsHtml() {
  if (orgError) return card('Family, team and league', `<p class="acct-err">${esc(orgError)}</p>`);
  return wardsCard() + (orgs.length ? orgs.map(orgCard).join('') : plansCard());
}

/** The children this account is the confirmed parent of (worker/vetting.js, the parent link). */
function wardsCard() {
  if (!wards.length) return '';
  return card('Your children on Hooper Buddy', `
    <p class="acct-note">You confirmed you are their parent or guardian. You see them in any family or team you are both in, and nobody else's parents do. Remove the link and you stop seeing them at once.</p>
    <ul class="people">${wards.map((w) => `<li>
      <span class="lab"><b>${esc(w.name)}</b><small>${w.age === 'kid' ? 'Under 13' : w.age === 'teen' ? '13 to 17' : 'Age not set'}${w.since ? ` &middot; linked ${esc(fmtShort(w.since))}` : ''}</small></span>
      <button class="btn small danger" data-unward="${esc(w.accountId)}" data-who="${esc(w.name)}">Remove link</button>
      <button class="btn small danger" data-eraseward="${esc(w.accountId)}" data-who="${esc(w.name)}">Delete account</button>
    </li>`).join('')}</ul>`);
}

function plansCard() {
  const rows = GROUP_PLANS.filter((id) => plans[id]).map((id) => {
    const p = plans[id];
    return `<div class="plan">
      <div class="plan-top">
        <div><b>${esc(p.name)}</b><small>$${esc(p.price)} a month per ${esc(p.per)}</small></div>
        <button class="btn primary" data-make="${id}">Create</button>
      </div>
      <p class="acct-note">${esc(p.blurb || '')}</p>
      <p class="acct-note">${esc(p.includedSeats)} player seats included${p.extraSeat ? `, $${esc(p.extraSeat)} a month for each extra seat` : ''}.</p>
    </div>`;
  }).join('');
  return card('Family, team and league', `
    <p class="acct-lead">Train together and keep one bill.</p>
    <p class="acct-note">A group gives a parent or a coach one place to see how their players are doing. Every player decides what the group sees and for how long, and can shut it off in a tap.</p>
    <div class="plans">${rows}</div>
    <p class="acct-note">Checkout is not connected yet, so a group you start now runs as a trial. Nothing is charged and no card is asked for.</p>`);
}

function orgCard(o) {
  const used = seatsUsed(o);
  const left = Math.max(0, (o.seats || 0) - used);
  // A team or league invite needs a verified coach (worker/orgs.js); the server decides, this only hides a form that would be refused.
  const canInvite = (o.myRole === 'owner' || o.myRole === 'coach') && !o.verification?.needed;
  const canView = VIEWER_ROLES.includes(o.myRole);
  return `<div class="card acct org" data-org="${esc(o.id)}" style="margin-bottom:14px">
    <h3>${esc(planName(o.type))}</h3>
    <div class="acct-head">
      <div class="lab"><b>${esc(o.name)}</b><small>You are the ${esc((ROLE_WORD[o.myRole] || 'member').toLowerCase())} here.</small></div>
      <span class="pill ${left ? 'good' : 'hot'}">${used} of ${esc(o.seats)} seats</span>
    </div>
    <p class="acct-note">${used} player ${used === 1 ? 'seat is' : 'seats are'} in use and ${left} ${left === 1 ? 'is' : 'are'} open. Parents and coaches do not use a player seat.</p>
    ${o.billing?.status === 'trial' ? '<p class="acct-note">Billing is not connected yet, so this group is running as a trial.</p>' : ''}
    ${o.verification?.needed ? `<div class="org-sec">
      <h4>Coach verification</h4>
      <p class="acct-lead">You hold this seat, and no player under 18 shows on this group's roster or in its shared view for you until your verification is approved.</p>
      <p class="acct-note">Every coach passes identity, a background check with the sex offender registry, abuse prevention training, their credential, two references, our code of conduct and their work email. A person checks each one by hand, and it can take a while. Until then you can still see adults who share with the group, and you cannot invite anyone into a team or league.</p>
      <div class="acct-row"><button class="btn" data-verify>Open verification</button></div>
    </div>` : ''}
    ${peopleHtml(o)}
    ${canInvite ? inviteHtml(o, left) : ''}
    ${shareHtml(o)}
    ${canView ? brainHtml(o) : ''}
    ${o.myRole === 'owner' ? auditHtml(o) : ''}
    ${o.myRole === 'owner'
      ? '<p class="acct-note">You started this group, so you cannot leave it. Remove the members you need to and it goes quiet.</p>'
      : '<div class="acct-row"><button class="btn danger" data-leave>Leave this group</button></div>'}
  </div>`;
}

function peopleHtml(o) {
  const amOwner = o.myRole === 'owner';
  const people = o.members.map((m) => {
    const mine = me && m.accountId === me.id;
    const canRemove = amOwner && !mine && m.role !== 'owner';
    // No label: an adult a player under 18 sees by role only (worker/orgs.js sightOf).
    const name = m.email || m.label || 'An adult in this group';
    return `<li>
      <span class="lab"><b>${esc(name)}${mine ? ' (you)' : ''}</b><small>${esc(ROLE_WORD[m.role] || m.role)} &middot; joined ${esc(fmtShort(m.joinedAt))}</small></span>
      ${canRemove ? `<button class="btn small danger" data-remove="${esc(m.accountId)}" data-who="${esc(name)}">Remove</button>` : ''}
      ${!mine ? `<button class="btn small" data-report="${esc(m.accountId)}" data-who="${esc(name)}">Report</button>` : ''}
    </li>`;
  }).join('');
  const waiting = (o.invites || []).map((i) => `<li>
      <span class="lab"><b>${esc(i.email)}</b><small>Invited as ${esc((ROLE_WORD[i.role] || i.role).toLowerCase())} on ${esc(fmtShort(i.at))}</small></span>
      <span class="pill cool">Pending</span>
    </li>`).join('');
  return `<div class="org-sec">
    <h4>Who is in it</h4>
    <ul class="people">${people}</ul>
    ${o.hiddenWhy ? `<p class="acct-note">${esc(o.hiddenWhy)}</p>` : ''}
    ${waiting ? `<h4>Waiting on</h4><ul class="people">${waiting}</ul>` : ''}
    <p class="acct-note">Report anyone here and a person reads it. A coach, scout or agent reported for a safety reason loses access to every player under 18 while it is reviewed.</p>
  </div>`;
}

function inviteHtml(o, left) {
  return `<div class="org-sec">
    <h4>Invite someone</h4>
    <div class="acct-row">
      <input class="acct-input grow" type="email" data-inv-email placeholder="their@email.com" maxlength="200">
      <select class="acct-input" data-inv-role>
        <option value="player">Player</option>
        <option value="parent">Parent</option>
        <option value="coach">Coach</option>
      </select>
      <button class="btn" data-invite>Send the invite</button>
    </div>
    <p class="acct-note">They get an email with a link that lasts seven days. ${left ? `${left} player ${left === 1 ? 'seat' : 'seats'} left.` : 'Every player seat is taken, so only parents and coaches can be added right now.'}</p>
  </div>`;
}

function shareHtml(o) {
  const s = myShare(o.id);
  const on = live(s);
  const d = draftFor(o);
  const now = on
    ? `Right now this group can see ${esc(labelList(s.scopes))}. It stops on ${esc(fmtDay(s.until))}.`
    : (s && s.scopes?.length
      ? `Your last share ran out on ${esc(fmtDay(s.until))}, so this group sees nothing right now.`
      : 'This group sees nothing about you right now.');
  return `<div class="org-sec share">
    <h4>What this group can see about me</h4>
    <p class="acct-lead">${now}</p>
    ${SCOPES.map((sc) => `<div class="srow">
      <div class="lab"><b>${sc.label}</b><small>${sc.note}</small></div>
      <div class="sw ${d.scopes.has(sc.id) ? 'on' : ''}" data-scope="${sc.id}" role="button" tabindex="0" aria-label="${sc.label}"></div>
    </div>`).join('')}
    <div class="srow">
      <div class="lab"><b>For how long</b><small>Sharing ends by itself when the time is up. Nobody has to remember to turn it off.</small></div>
      <select class="acct-input" data-days>
        ${DAYS.map(([v, l]) => `<option value="${v}"${v === d.days ? ' selected' : ''}>${l}</option>`).join('')}
      </select>
    </div>
    ${whoami.age === 'unknown' ? '<p class="acct-note"><b>No age is set, so nothing is shared and nobody here sees this player.</b> Choose the age range in Settings first.</p>' : ''}
    ${whoami.age === 'kid' && !whoami.consent ? '<p class="acct-note"><b>Under 13, a parent confirms their email first</b> (Settings, Parent or guardian). Until then nothing is shared and nobody here sees this player.</p>' : ''}
    ${isMinor() ? `<div class="srow">
      <div class="lab"><b>Parent PIN</b><small>Under 18, a parent decides what a group sees. Set the PIN below in Settings if there is none yet.</small></div>
      <input class="acct-input" type="password" data-share-pin maxlength="8" placeholder="PIN" style="max-width:120px">
    </div>` : ''}
    <div class="acct-row">
      <button class="btn primary" data-share-save>Save sharing</button>
      ${on ? '<button class="btn danger" data-share-revoke>Revoke</button>' : ''}
    </div>
    <p class="acct-note">${isMinor() ? 'Only a parent with the PIN can change this. Revoke it any second and the group stops seeing it that moment.' : 'You are the only one who can change this. Revoke it any second and the group stops seeing you that moment.'}</p>
  </div>`;
}

const labelList = (ids) => {
  const names = (ids || []).map((id) => (SCOPES.find((s) => s.id === id)?.label || id).toLowerCase());
  if (names.length <= 1) return names[0] || 'nothing';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
};

/* --------------------------------------------------------------- shared brain */
function brainHtml(o) {
  const data = brains.get(o.id);
  return `<div class="org-sec">
    <h4>Shared brain</h4>
    <p class="acct-note">What the players in this group have chosen to share, and nothing else. A player under 18 shows here only to a verified coach of the group and to their own confirmed parent.</p>
    <div class="acct-row"><button class="btn" data-brain>${data ? 'Refresh the shared brain' : 'Open the shared brain'}</button></div>
    ${data ? (data.error ? `<p class="acct-err">${esc(data.error)}</p>` : brainBody(data.players) + (data.hiddenWhy ? `<p class="acct-note">${esc(data.hiddenWhy)}</p>` : '')) : ''}
  </div>`;
}

function brainBody(players) {
  if (!players || !players.length) return '<p class="acct-note">No players in this group yet. Invite one and they show up here.</p>';
  return `<div class="brain">${players.map(brainPlayer).join('')}</div>`;
}

function brainPlayer(p) {
  const who = esc(p.name || p.email || 'Player');
  if (!p.sharing || !p.sharing.length) {
    return `<div class="bp"><div class="lab"><b>${who}</b><small>Not sharing right now. That is their call, and it is normal.</small></div></div>`;
  }
  const bits = [];
  if (p.recall) {
    const lt = p.recall.lifetime;
    bits.push(lt
      ? `<div class="grid-stats">
          <div class="stat"><b>${esc(p.recall.sessions)}</b><small>sessions</small></div>
          <div class="stat"><b>${esc(lt.makes)}/${esc(lt.attempts)}</b><small>lifetime</small></div>
          <div class="stat"><b>${esc(lt.elbow)}</b><small>elbow</small></div>
          <div class="stat"><b>${esc(lt.release)}</b><small>release</small></div>
          <div class="stat"><b>${esc(lt.follow)}</b><small>follow</small></div>
        </div>`
      : '<p class="acct-note">No sessions logged yet.</p>');
  }
  if (p.sessions?.length) {
    bits.push(`<h5>Recent sessions</h5><ul class="mini">${p.sessions.slice(0, 6).map((s) =>
      `<li><b>${esc(fmtShort(s.startedAt))}</b> ${esc(s.makes)} of ${esc(s.attempts)}, form ${esc(s.avgForm)}</li>`).join('')}</ul>`);
  }
  if (p.journal?.length) {
    bits.push(`<h5>Journal</h5><ul class="mini">${p.journal.slice(0, 6).map((e) =>
      `<li><b>${esc(fmtShort(e.at))}</b> ${esc(cut(e.text, 160))}</li>`).join('')}</ul>`);
  }
  if (p.nutrition) {
    const n = p.nutrition;
    const ml = Number(n.waterMl) || 0;
    bits.push(`<h5>Nutrition, last ${esc(n.days || 7)} days</h5>
      <div class="grid-stats">
        <div class="stat"><b>${esc(n.meals || 0)}</b><small>meals</small></div>
        <div class="stat"><b>${esc(Math.round(n.calories || 0))}</b><small>calories</small></div>
        <div class="stat"><b>${esc(Math.round(n.protein || 0))}g</b><small>protein</small></div>
        <div class="stat"><b>${ml >= 1000 ? `${(ml / 1000).toFixed(1)}L` : `${Math.round(ml)}ml`}</b><small>water</small></div>
      </div>`);
  }
  if (p.body?.length) {
    const b = p.body[0];
    const line = [b.weightKg && `${b.weightKg} kg`, b.heightCm && `${b.heightCm} cm`, b.bmi && `BMI ${b.bmi}`,
      b.sleepHours != null && `${b.sleepHours} h sleep`, b.restingHr && `${b.restingHr} bpm resting`].filter(Boolean).join(' · ');
    bits.push(`<h5>Body</h5><ul class="mini"><li><b>${esc(fmtShort(b.at))}</b> ${esc(line || 'logged')}</li></ul>`);
  }
  if (p.vault?.length) {
    bits.push(`<h5>Vault clips</h5><ul class="mini">${p.vault.slice(0, 6).map((c) =>
      `<li><b>${esc(fmtShort(c.at))}</b> ${esc(c.attempts ?? 0)} attempts, ${esc(c.makes ?? 0)} makes, form ${esc(c.form ?? '?')}</li>`).join('')}</ul>`);
  }
  return `<div class="bp">
    <div class="lab"><b>${who}</b><small>Sharing ${esc(labelList(p.sharing))}${p.until ? ` until ${esc(fmtDay(p.until))}` : ''}</small></div>
    ${bits.join('') || '<p class="acct-note">Nothing logged in what they shared yet.</p>'}
  </div>`;
}

/* ---------------------------------------------------------------- audit log */
function auditHtml(o) {
  const data = audits.get(o.id);
  const rows = Array.isArray(data) ? data : null;
  return `<div class="org-sec">
    <h4>Activity log</h4>
    <p class="acct-note">Every invite, every join and every look at the shared brain is written down here.</p>
    <div class="acct-row"><button class="btn" data-audit>${data ? 'Refresh the activity log' : 'Show the activity log'}</button></div>
    ${data && data.error ? `<p class="acct-err">${esc(data.error)}</p>` : ''}
    ${rows ? (rows.length
      ? `<ul class="mini">${rows.slice(0, 60).map((a) => `<li><b>${esc(fmtShort(a.at))}</b> ${esc(whoIs(o, a.who))} ${esc(a.what || '')}</li>`).join('')}</ul>`
      : '<p class="acct-note">Nothing has happened in this group yet.</p>') : ''}
  </div>`;
}

const whoIs = (o, id) => { const m = o.members.find((x) => x.accountId === id); return m?.email || m?.label || 'someone'; };

/* ------------------------------------------------------------------- events */
function bind() {
  if (!root) return;
  const $ = (sel) => root.querySelector(sel);
  const all = (sel) => Array.from(root.querySelectorAll(sel));

  $('#acctSend')?.addEventListener('click', () => sendLink());
  $('#acctEmail')?.addEventListener('input', (e) => { typedEmail = e.target.value; });
  $('#acctEmail')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') sendLink(); });
  $('#acctAgain')?.addEventListener('click', () => { const to = sentTo; typedEmail = to; sentTo = ''; paint(); sendLink(to); });
  $('#acctOther')?.addEventListener('click', () => { sentTo = ''; typedEmail = ''; paint(); });

  $('#acctNameSave')?.addEventListener('click', saveName);
  $('#acctDigest')?.addEventListener('change', (e) => saveDigest(e.target.value));
  $('#acctOut')?.addEventListener('click', signOut);

  all('[data-make]').forEach((b) => b.addEventListener('click', () => createOrg(b.dataset.make, b)));

  all('[data-org]').forEach((cardEl) => {
    const id = cardEl.dataset.org;
    const o = orgs.find((x) => x.id === id);
    if (!o) return;
    const d = draftFor(o);
    cardEl.querySelectorAll('[data-scope]').forEach((sw) => {
      const flip = () => {
        if (d.scopes.has(sw.dataset.scope)) d.scopes.delete(sw.dataset.scope); else d.scopes.add(sw.dataset.scope);
        sw.classList.toggle('on', d.scopes.has(sw.dataset.scope));
      };
      sw.addEventListener('click', flip);
      sw.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); } });
    });
    cardEl.querySelector('[data-days]')?.addEventListener('change', (e) => { d.days = Number(e.target.value) || 30; });
    const pinOf = () => cardEl.querySelector('[data-share-pin]')?.value || undefined;
    cardEl.querySelector('[data-share-save]')?.addEventListener('click', (e) => saveShare(o, [...d.scopes], d.days, e.currentTarget, pinOf()));
    cardEl.querySelector('[data-share-revoke]')?.addEventListener('click', (e) => saveShare(o, [], d.days, e.currentTarget, pinOf()));
    cardEl.querySelector('[data-invite]')?.addEventListener('click', (e) => invite(o, cardEl, e.currentTarget));
    cardEl.querySelector('[data-brain]')?.addEventListener('click', (e) => loadBrain(o, e.currentTarget));
    cardEl.querySelector('[data-audit]')?.addEventListener('click', (e) => loadAudit(o, e.currentTarget));
    cardEl.querySelector('[data-leave]')?.addEventListener('click', () => leave(o));
    cardEl.querySelectorAll('[data-remove]').forEach((b) =>
      b.addEventListener('click', () => removeMember(o, b.dataset.remove, b.dataset.who)));
    cardEl.querySelectorAll('[data-report]').forEach((b) =>
      b.addEventListener('click', () => reportMember(b.dataset.report, b.dataset.who)));
    cardEl.querySelector('[data-verify]')?.addEventListener('click', () => ctx.go('scouts'));
  });
  all('[data-unward]').forEach((b) => b.addEventListener('click', () => removeWard(b.dataset.unward, b.dataset.who)));
  all('[data-eraseward]').forEach((b) => b.addEventListener('click', () => eraseWard(b.dataset.eraseward, b.dataset.who)));
}

function say(msg) {
  const el = root?.querySelector('#acctErr');
  if (!el) return;
  el.textContent = msg || '';
  el.hidden = !msg;
}

async function busy(btn, label, fn) {
  if (!btn) return fn();
  const was = btn.textContent;
  btn.disabled = true; btn.textContent = label;
  try { return await fn(); }
  finally { if (btn.isConnected) { btn.disabled = false; btn.textContent = was; } }
}

/* ------------------------------------------------------------------ actions */
async function sendLink(preset) {
  const input = root?.querySelector('#acctEmail');
  const email = String(preset || input?.value || '').trim();
  if (!email) { say('Enter your email first.'); return; }
  typedEmail = email;
  const btn = root?.querySelector('#acctSend');
  say('');
  await busy(btn, 'Sending…', async () => {
    try {
      await ctx.api('/api/auth/start', { method: 'POST', body: JSON.stringify({ email }) });
      sentTo = email; typedEmail = '';
      paint();
      ctx.toast('Link sent. Check your inbox.');
    } catch (e) { say(e.message); ctx.toast(e.message, true); }
  });
}

async function saveName() {
  const input = root?.querySelector('#acctName');
  const name = String(input?.value || '').trim();
  if (!name) { ctx.toast('Give yourself a name first.', true); return; }
  const btn = root?.querySelector('#acctNameSave');
  await busy(btn, 'Saving…', async () => {
    try {
      const r = await ctx.api('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) });
      me.name = r.name ?? name;
      ctx.toast('Name saved.');
    } catch (e) { ctx.toast(e.message, true); }
  });
}

async function saveDigest(frequency) {
  try {
    const r = await ctx.api('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ digest: { frequency } }) });
    me.digest = r.digest || { frequency };
    ctx.toast(frequency === 'off' ? 'Recap emails are off.' : `Recap emails set to ${frequency}.`);
  } catch (e) { ctx.toast(e.message, true); await render(); }
}

async function signOut() {
  try { await ctx.api('/api/auth/logout', { method: 'POST' }); }
  catch (e) { ctx.toast(e.message, true); return; }
  location.reload();
}

/** The rail's door button: end the session when there is one, otherwise go home. */
async function railSignOut() {
  let who = me;
  if (!who) { try { who = await ctx.api('/api/auth/me'); } catch (e) { who = null; } }
  if (who && who.signedIn) { await signOut(); return; }
  location.href = '/';
}

async function createOrg(type, btn) {
  const p = plans[type];
  const name = await ctx.ui.prompt({
    title: `Name your ${String(p?.name || type).toLowerCase()}`,
    body: 'Players and parents see this name on their invite, so make it one they will recognise.',
    label: 'Name', placeholder: type === 'family' ? 'The Baptiste family' : 'Eastside 14U',
    confirmText: 'Create',
  });
  if (!name || !String(name).trim()) return;
  await busy(btn, 'Creating…', async () => {
    try {
      await ctx.api('/api/orgs', { method: 'POST', body: JSON.stringify({ type, name: String(name).trim() }) });
      ctx.toast(`${plain(name)} is set up.`);
      await render();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

async function invite(o, cardEl, btn) {
  const email = String(cardEl.querySelector('[data-inv-email]')?.value || '').trim();
  const role = cardEl.querySelector('[data-inv-role]')?.value || 'player';
  if (!email) { ctx.toast('Enter their email first.', true); return; }
  await busy(btn, 'Sending…', async () => {
    try {
      const r = await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/invite`, { method: 'POST', body: JSON.stringify({ email, role }) });
      ctx.toast(`Invite sent to ${email}.`);
      // The Worker only hands back a link when email sending is not configured.
      if (r.link) await ctx.ui.alert({ title: 'Email is not set up yet', body: `Send them this link yourself. It lasts seven days.\n\n${r.link}`, confirmText: 'Got it' });
      await loadOrgs(); paint();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

async function saveShare(o, scopes, days, btn, pin) {
  await busy(btn, 'Saving…', async () => {
    try {
      const r = await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/share`, { method: 'POST', body: JSON.stringify({ scopes, days, pin }) });
      shares = Array.isArray(r.shares) ? r.shares : [];
      drafts.delete(o.id);
      ctx.toast(scopes.length ? 'Sharing saved.' : 'Sharing revoked. They see nothing now.');
      paint();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

async function loadBrain(o, btn) {
  await busy(btn, 'Loading…', async () => {
    try {
      const r = await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/brain`);
      brains.set(o.id, { players: Array.isArray(r.players) ? r.players : [], hiddenWhy: r.hiddenWhy || '' });
    } catch (e) { brains.set(o.id, { error: e.message }); }
    paint();
  });
}

async function loadAudit(o, btn) {
  await busy(btn, 'Loading…', async () => {
    try {
      const r = await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/audit`);
      audits.set(o.id, Array.isArray(r) ? r : []);
    } catch (e) { audits.set(o.id, { error: e.message }); }
    paint();
  });
}

async function removeMember(o, accountId, who) {
  const ok = await ctx.ui.confirm({
    title: `Remove ${plain(who) || 'this member'}?`,
    body: 'They lose access to this group and anything they were sharing with it stops. You can invite them again later.',
    confirmText: 'Remove', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/members/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
    ctx.toast('Removed.');
    brains.delete(o.id); audits.delete(o.id);
    await loadOrgs(); paint();
  } catch (e) { ctx.toast(e.message, true); }
}

async function leave(o) {
  if (!me?.id) return;
  const ok = await ctx.ui.confirm({
    title: `Leave ${plain(o.name)}?`,
    body: 'You stop sharing with this group right away and it drops off your settings. Everything you have logged stays yours.',
    confirmText: 'Leave', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/orgs/${encodeURIComponent(o.id)}/members/${encodeURIComponent(me.id)}`, { method: 'DELETE' });
    ctx.toast(`You left ${plain(o.name)}.`);
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

/** Report someone in the group for a safety concern (worker/social.js, worker/vetting.js). */
async function reportMember(accountId, who) {
  const pick = await ctx.ui.prompt({
    title: `Report ${plain(who) || 'this person'}?`,
    body: `What happened? Type the number.\n${REPORT_REASONS.map(([, l], i) => `${i + 1}. ${l}`).join('\n')}\n\nA person reads every report. Reasons 1 to 4 are safety reasons: a coach, scout or agent reported for one loses access to every player under 18 while it is reviewed.`,
    label: 'Number', placeholder: '1', confirmText: 'Next',
  });
  const reason = REPORT_REASONS[Number(pick) - 1]?.[0];
  if (!reason) return;
  const details = (await ctx.ui.prompt({ title: 'A few words (optional)', body: 'Only Hooper Buddy sees this.', label: 'What happened', placeholder: '', confirmText: 'Send report' })) || '';
  try {
    await ctx.api('/api/social/report', { method: 'POST', body: JSON.stringify({ accountId, reason, details }) });
    ctx.toast('Report sent. A person reads every report.');
  } catch (e) { ctx.toast(e.message, true); }
}

async function removeWard(accountId, who) {
  const ok = await ctx.ui.confirm({
    title: `Remove your link to ${plain(who) || 'this player'}?`,
    body: 'You stop seeing them in your family or team at once. Under 13, nothing about them is visible to anyone until a parent confirms again.',
    confirmText: 'Remove link', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/orgs/wards/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
    ctx.toast('Link removed.');
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

/** A parent deletes their child's account and everything in it (worker/erase.js). */
async function eraseWard(accountId, who) {
  const typed = await ctx.ui.prompt({
    title: `Delete ${plain(who) || 'this player'}'s account?`,
    body: 'Every session, note, meal, clip and minute on their account is removed, and they are taken out of every family and team. This cannot be undone. Type DELETE to confirm.',
    placeholder: 'DELETE', confirmText: 'Delete account', danger: true,
  });
  if (typed == null) return;
  try {
    await ctx.api(`/api/orgs/wards/${encodeURIComponent(accountId)}/account`, { method: 'DELETE', body: JSON.stringify({ confirm: String(typed).trim() }) });
    ctx.toast('Their account is deleted.');
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}
